import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullScreenPhotoViewer from './FullScreenPhotoViewer';

/**
 * PhotoCarousel component for displaying hike photos.
 *
 * Features:
 * - Swipe navigation on mobile (touch events)
 * - Arrow buttons on desktop
 * - Dot indicators
 * - Lazy loading with spinner
 * - First photo loads immediately (priority)
 * - Keyboard navigation (left/right arrows)
 * - Full-screen photo viewer with zoom
 *
 * @param {Object[]} photos - Array of photo objects with media_url, thumbnail_url, medium_thumbnail_url
 * @param {string} className - Additional CSS classes
 * @param {boolean} useThumbnails - Whether to use small thumbnail_url (default: false)
 * @param {boolean} useMediumThumbnails - Whether to use medium_thumbnail_url (default: true)
 */
function PhotoCarousel({ photos = [], className = '', useThumbnails = false, useMediumThumbnails = true }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [loadedImages, setLoadedImages] = useState(new Set([0])); // First image loaded by default
    const [fullScreenIndex, setFullScreenIndex] = useState(null);
    const carouselRef = useRef(null);

    // Minimum swipe distance (in px) to trigger navigation
    const minSwipeDistance = 50;

    const goToNext = useCallback(() => {
        setCurrentIndex((prevIndex) =>
            prevIndex === photos.length - 1 ? 0 : prevIndex + 1
        );
    }, [photos.length]);

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? photos.length - 1 : prevIndex - 1
        );
    }, [photos.length]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                goToPrevious();
            } else if (e.key === 'ArrowRight') {
                goToNext();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [goToNext, goToPrevious]);

    // Preload current image and adjacent images
    useEffect(() => {
        setLoadedImages(prev => {
            const next = new Set(prev);
            next.add(currentIndex);
            if (currentIndex > 0) next.add(currentIndex - 1);
            if (currentIndex < photos.length - 1) next.add(currentIndex + 1);
            // Avoid re-render if nothing changed
            if (next.size === prev.size) return prev;
            return next;
        });
    }, [currentIndex, photos.length]);

    if (!photos || photos.length === 0) {
        return (
            <div className={`bg-gray-200 flex items-center justify-center ${className}`}>
                <p className="text-gray-500">No photos available</p>
            </div>
        );
    }

    const goToIndex = (index) => {
        setCurrentIndex(index);
    };

    // Touch event handlers for swipe
    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            goToNext();
        } else if (isRightSwipe) {
            goToPrevious();
        }
    };

    const getImageUrl = (photo) => {
        // Priority: small thumbnail > medium thumbnail > full resolution
        if (useThumbnails && photo.thumbnail_url) {
            return photo.thumbnail_url;
        }
        // Use medium thumbnail for carousel display (default)
        if (useMediumThumbnails && photo.medium_thumbnail_url) {
            return photo.medium_thumbnail_url;
        }
        // Fallback to full resolution
        return photo.media_url;
    };

    return (
        <div className={`relative overflow-hidden ${className}`} ref={carouselRef}>
            {/* Images container */}
            <div
                className="flex transition-transform duration-300 ease-out h-full"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                {photos.map((photo, index) => (
                    <div
                        key={photo.id || index}
                        className="w-full h-full flex-shrink-0 relative"
                    >
                        {loadedImages.has(index) ? (
                            <div className="w-full h-full relative">
                                <img
                                    src={getImageUrl(photo)}
                                    alt={`Slide ${index + 1}`}
                                    className="w-full h-full object-cover cursor-pointer"
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                    onClick={() => {
                                    // Ensure index is within bounds before opening full-screen
                                    if (index >= 0 && index < photos.length) {
                                        setFullScreenIndex(index);
                                    }
                                }}
                                />
                            </div>
                        ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Navigation arrows (desktop) */}
            {photos.length > 1 && (
                <>
                    <button
                        onClick={goToPrevious}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all hidden md:block"
                        aria-label="Previous photo"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>

                    <button
                        onClick={goToNext}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all hidden md:block"
                        aria-label="Next photo"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </>
            )}

            {/* Dot indicators */}
            {photos.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {photos.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                                index === currentIndex
                                    ? 'bg-white w-6'
                                    : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                            }`}
                            aria-label={`Go to photo ${index + 1}`}
                        />
                    ))}
                </div>
            )}

            {/* Photo counter */}
            {photos.length > 1 && (
                <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                    {currentIndex + 1} / {photos.length}
                </div>
            )}
            
            {/* Full-screen photo viewer */}
            <FullScreenPhotoViewer
                photos={photos}
                initialIndex={fullScreenIndex}
                show={fullScreenIndex !== null}
                onClose={() => setFullScreenIndex(null)}
            />
        </div>
    );
}

export default PhotoCarousel;
